import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Spinner } from '@trello/nachos/spinner';
import type { SecureString } from '@trello/privacy';
import {
  dangerouslyConvertPrivacyString,
  EMPTY_SECURE_STRING,
} from '@trello/privacy';
import type { AppManagementTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { usePluginApplicationQuery } from '../queries/PluginApplicationQuery.generated';
import { AllowedOrigins } from './AllowedOrigins';
import { PowerUpGenerateApiKeyPage } from './PowerUpGenerateApiKeyPage';
import { TextFieldDescription } from './TextFieldDescription';

import styles from './PowerUpApiKeyPage.less';

export const PowerUpApiKeyPage: FunctionComponent<{
  idPlugin: string;
}> = ({ idPlugin }) => {
  const intl = useIntl();
  // eslint-disable-next-line @trello/no-apollo-refetch
  const { data, loading, refetch } = usePluginApplicationQuery({
    variables: {
      pluginId: idPlugin,
    },
    waitOn: ['None'],
  });
  const [apiKey, setApiKey] = useState<string>('');
  const [secret, setSecret] = useState<SecureString>(EMPTY_SECURE_STRING);
  const [generated, setGenerated] = useState<boolean>(false);
  const onGenerate = useCallback(
    () => {
      refetch();
      setGenerated(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (data && !loading) {
      setApiKey(data.pluginApplication?.key || '');
      setSecret(data.pluginApplication?.secret || EMPTY_SECURE_STRING);
      setGenerated(true);
    }
  }, [data, loading]);

  if (loading) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner
          testId={getTestId<AppManagementTestIds>(
            'power-up-appkey-page-spinner',
          )}
        />
      </div>
    );
  }

  if (!generated) {
    return (
      <PowerUpGenerateApiKeyPage idPlugin={idPlugin} onGenerate={onGenerate} />
    );
  }

  return (
    <div>
      <div className={styles.header}>
        <h1>
          <FormattedMessage
            id="templates.app_management.api-key"
            defaultMessage="API key"
            description="Title for the API key page"
          />
        </h1>
      </div>
      <div className={styles.formElements}>
        <TextFieldDescription
          label={intl.formatMessage({
            id: 'templates.app_management.api-key',
            defaultMessage: 'API key',
            description: 'Label for the API key field',
          })}
          value={apiKey}
          description={
            <FormattedMessage
              id="templates.app_management.api-key-management-description"
              defaultMessage="Most developers will need to ask each user to <link1>authorize</link1> your application. If you are looking to build an application for yourself, or are doing local testing, you can manually generate a <link2>Token</link2>."
              description="Description for API key management"
              values={{
                link1: (chunks) => (
                  <a href="https://developer.atlassian.com/cloud/trello/guides/rest-api/authorization/">
                    {chunks}
                  </a>
                ),
                link2: (chunks) => (
                  <a
                    href={`/1/authorize?expiration=never&scope=read,write,account&response_type=token&key=${apiKey}`}
                    target="_blank"
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          }
        />

        <AllowedOrigins
          apiKey={apiKey}
          origins={data?.pluginApplication?.allowedOrigins || []}
        />

        <TextFieldDescription
          label={intl.formatMessage({
            id: 'templates.app_management.secret',
            defaultMessage: 'Secret',
            description: 'Label for the secret field',
          })}
          value={dangerouslyConvertPrivacyString(secret)}
          description={
            <>
              <FormattedMessage
                id="templates.app_management.app-secret-trello-supports-oauth"
                defaultMessage="Trello supports OAuth 1 <a>authorization</a>, if you would like to use this, you will need your Secret."
                description="Description for OAuth support"
                values={{
                  a: (chunks) => (
                    <a href="https://developers.trello.com/authorize">
                      {chunks}
                    </a>
                  ),
                }}
              />
              <br />
              <br />
              <FormattedMessage
                id="templates.app_management.please-keep-your-api-secret-safe"
                defaultMessage="Please keep your API Secret safe. Because your API Key is public for any client-side applications, we do not currently offer a way to reset it."
                description="Warning about keeping API secret safe"
              />
            </>
          }
        />
      </div>
    </div>
  );
};
